// The checks the rep sees on site, before anything is sent. They mirror the server's rules
// (apps/api/factory/validation.py) for what can go wrong in the field: missing measurements,
// a room that does not close, values that look like a unit mistake, and items that do not fit
// their wall. The server stays the authority: when online, the app also asks it
// (POST /v1/factory/surveys/validate) and shows its messages.

import { type Project, type RoomDraft, cornerLabel, wallLabel } from "../project/model";
import { selfIntersections, twiceSignedArea } from "./geometry";
import { type SolveEdge, solveRoom } from "./solve";
import { formatDelta, formatMm } from "./units";

export const TOLERANCE = {
  wallUm: 300,
  closureUm: 5000,
  placementUm: 300,
};

const WALL_MIN = 100_000;
const WALL_MAX = 30_000_000;
const CEILING_MIN = 1_800_000;
const CEILING_MAX = 6_000_000;
const ZONE_DEPTH_MAX = 1_500_000;

export interface FieldIssue {
  code: string;
  severity: "error" | "warning";
  room?: string;
  /** what to tap to fix it: a wall index, a diagonal, an item id */
  target?: { wall?: number; diagonal?: [number, number]; item?: string };
  message: string;
}

const edgeName = (e: SolveEdge) =>
  e.kind === "wall" ? `${wallLabel(e.index)} хана` : `${cornerLabel(e.a)}–${cornerLabel(e.b)} диагональ`;

export function reviewRoom(room: RoomDraft, tol = TOLERANCE): FieldIssue[] {
  const out: FieldIssue[] = [];
  const err = (code: string, message: string, target?: FieldIssue["target"]) =>
    out.push({ code, severity: "error", room: room.id, message, ...(target ? { target } : {}) });
  const warn = (code: string, message: string, target?: FieldIssue["target"]) =>
    out.push({ code, severity: "warning", room: room.id, message, ...(target ? { target } : {}) });
  const n = room.sketch.length;

  room.walls.forEach((w, i) => {
    if (!w) err("missing_wall", `${wallLabel(i)} ханын уртыг хэмжинэ үү.`, { wall: i });
    else if (w.um < WALL_MIN || w.um > WALL_MAX)
      err("implausible_value", `${wallLabel(i)} хана ${formatMm(w.um)} байна: 100 мм-ээс 30 м-ийн хооронд байх ёстой.`, { wall: i });
    room.heights[i]?.forEach((h) => {
      if (h.um < CEILING_MIN || h.um > CEILING_MAX)
        err("implausible_value", `${wallLabel(i)} ханын өндөр ${formatMm(h.um)} байна: 1.8-6 м байх ёстой.`, { wall: i });
    });
  });
  if (!room.ceiling) err("missing_ceiling", "Таазны хамгийн нам өндрийг хэмжинэ үү.");
  else if (room.ceiling.um < CEILING_MIN || room.ceiling.um > CEILING_MAX)
    err("implausible_value", `Таазны өндөр ${formatMm(room.ceiling.um)} байна: 1.8-6 м байх ёстой.`);

  for (const d of room.diagonals) {
    if (!d.length) err("missing_diagonal", `${cornerLabel(d.a)}–${cornerLabel(d.b)} диагоналийг хэмжинэ үү.`, { diagonal: [d.a, d.b] });
  }

  const solved = solveRoom({
    sketch: room.sketch,
    walls: room.walls.map((w) => w?.um ?? null),
    diagonals: room.diagonals.map((d) => ({ a: d.a, b: d.b, um: d.length?.um ?? null })),
  });
  if (solved.ok) {
    const pts = solved.at;
    if (selfIntersections(pts).length) {
      err("polygon_self_intersecting", "Хэмжээнээс гарсан план өөрөө огтлолцож байна. Хэлбэр болон хананы уртыг шалгана уу.");
    } else if (twiceSignedArea(pts) >= 0n) {
      err("polygon_not_clockwise", "Хэлбэрийн булангуудыг цагийн зүүний дагуу тэмдэглэнэ үү.");
    }
    for (const c of solved.checks) {
      const limit = c.edge.kind === "wall" ? tol.wallUm : tol.closureUm;
      if (Math.abs(c.delta) > limit) {
        err(
          c.edge.kind === "wall" ? "wall_length_mismatch" : "diagonal_mismatch",
          `Өрөө битүүрэхгүй байна: ${edgeName(c.edge)} ${formatMm(c.edge.um)} гэж хэмжигдсэн ч бусад хэмжээнээс ${formatMm(
            c.edge.um - c.delta,
          )} гарч байна (зөрүү ${formatDelta(c.delta)}, хүлцэл ${formatDelta(limit)}). Энэ диагональ болон түүний хоорондох ханануудыг дахин хэмжинэ үү.`,
          c.edge.kind === "wall" ? { wall: c.edge.index } : { diagonal: [c.edge.a, c.edge.b] },
        );
      }
    }
    if (n > 3 && !solved.checks.some((c) => c.edge.kind === "diagonal")) {
      warn("no_closure_check", "Шалгах диагональ алга: буруу уншилтыг илрүүлэхийн тулд дахин нэг диагональ хэмжинэ үү.");
    }
  } else if (solved.reason === "not_rigid") {
    const s = solved.suggest;
    err(
      "angles_not_fixed",
      `Өнцөг тодорхой болоогүй байна (90° гэж таамаглахгүй).${s ? ` ${cornerLabel(s[0])}–${cornerLabel(s[1])} диагоналийг хэмжинэ үү.` : ""}`,
      s ? { diagonal: s } : undefined,
    );
  } else if (solved.reason === "impossible_triangle") {
    err(
      "impossible_triangle",
      `${cornerLabel(solved.corner)}-р булан нийлэхгүй байна: ${solved.edges.map(edgeName).join(", ")} хэмжээ гурвалжин үүсгэхгүй. Эдгээрийг дахин хэмжинэ үү.`,
    );
  }

  const wallLen = (i: number) => room.walls[i]?.um ?? null;
  const ceiling = room.ceiling?.um ?? null;
  const heightsOf = (i: number) => [ceiling ?? 0, ...(room.heights[i] ?? []).map((h) => h.um)].filter((h) => h >= CEILING_MIN && h <= CEILING_MAX);
  const along = (label: string, wall: number, start: number, size: number, item: string) => {
    const len = wallLen(wall);
    if (len !== null && start + size > len + tol.placementUm)
      err("outside_wall", `${label} ${wallLabel(wall)} ханаас хэтэрч байна (${formatMm(start)} + ${formatMm(size)} > ${formatMm(len)}).`, { wall, item });
  };
  const vertical = (label: string, wall: number, bottom: number, size: number, item: string) => {
    if (ceiling === null || ceiling < CEILING_MIN || ceiling > CEILING_MAX) return;
    const hs = heightsOf(wall);
    const low = Math.min(...hs);
    const high = Math.max(...hs);
    const top = bottom + size;
    if (top > high + tol.placementUm) err("above_ceiling", `${label} ${formatMm(top)} өндөрт хүрч, таазнаас (${formatMm(high)}) давж байна.`, { wall, item });
    else if (top > low + tol.placementUm) warn("above_lowest_ceiling", `${label} таазны хамгийн нам цэгээс өндөр байна. Байрлалыг шалгана уу.`, { wall, item });
  };

  const boxes = new Map<number, { x0: number; x1: number; z0: number; z1: number; id: string }[]>();
  for (const o of room.openings) {
    if (!o.offset || !o.width || !o.height || (o.kind !== "door" && !o.sill)) {
      err("missing_value", `${o.id}: нээлхийн бүх хэмжээг оруулна уу.`, { wall: o.wall, item: o.id });
      continue;
    }
    if (o.width.um <= 0 || o.height.um <= 0) err("zero_size", `${o.id}: өргөн, өндөр 0 байж болохгүй.`, { item: o.id });
    const sill = o.sill?.um ?? 0;
    along(o.id, o.wall, o.offset.um, o.width.um, o.id);
    vertical(o.id, o.wall, sill, o.height.um, o.id);
    const list = boxes.get(o.wall) ?? [];
    for (const b of list) {
      if (o.offset.um < b.x1 && b.x0 < o.offset.um + o.width.um && sill < b.z1 && b.z0 < sill + o.height.um)
        err("openings_overlap", `${b.id} ба ${o.id} нээлхий давхцаж байна.`, { wall: o.wall, item: o.id });
    }
    list.push({ x0: o.offset.um, x1: o.offset.um + o.width.um, z0: sill, z1: sill + o.height.um, id: o.id });
    boxes.set(o.wall, list);
  }
  for (const u of room.utilities) {
    if (!u.offset || !u.height) {
      err("missing_value", `${u.id}: цэгийн байрлал, өндрийг оруулна уу.`, { wall: u.wall, item: u.id });
      continue;
    }
    along(u.id, u.wall, u.offset.um, 0, u.id);
    vertical(u.id, u.wall, u.height.um, 0, u.id);
  }
  for (const o of room.obstructions) {
    if (!o.offset || !o.width || !o.elevation || !o.height || !o.depth) {
      err("missing_value", `${o.id}: саадын бүх хэмжээг оруулна уу.`, { wall: o.wall, item: o.id });
      continue;
    }
    if (o.width.um <= 0 || o.height.um <= 0) err("zero_size", `${o.id}: өргөн, өндөр 0 байж болохгүй.`, { item: o.id });
    along(o.id, o.wall, o.offset.um, o.width.um, o.id);
    vertical(o.id, o.wall, o.elevation.um, o.height.um, o.id);
  }
  const zoneBoxes = new Map<number, { x0: number; x1: number; z0: number; z1: number; id: string }[]>();
  for (const z of room.zones) {
    const label = z.name || z.id;
    if (z.length <= 0 || z.height <= 0 || z.depth <= 0) err("zero_size", `${label}: урт, өндөр, гүн 0 байж болохгүй.`, { item: z.id });
    if (z.depth > ZONE_DEPTH_MAX) err("implausible_value", `${label}: гүн ${formatMm(z.depth)} хэт их байна (1.5 м хүртэл).`, { item: z.id });
    along(label, z.wall, z.offset, z.length, z.id);
    vertical(label, z.wall, z.elevation, z.height, z.id);
    const list = zoneBoxes.get(z.wall) ?? [];
    for (const b of list) {
      if (z.offset < b.x1 && b.x0 < z.offset + z.length && z.elevation < b.z1 && b.z0 < z.elevation + z.height)
        err("zones_overlap", `${b.id} ба ${label} бүс давхцаж байна.`, { wall: z.wall, item: z.id });
    }
    list.push({ x0: z.offset, x1: z.offset + z.length, z0: z.elevation, z1: z.elevation + z.height, id: label });
    zoneBoxes.set(z.wall, list);
    for (const o of room.openings) {
      if (o.wall !== z.wall || !o.offset || !o.width || !o.height) continue;
      const sill = o.sill?.um ?? 0;
      if (z.offset < o.offset.um + o.width.um && o.offset.um < z.offset + z.length && z.elevation < sill + o.height.um && sill < z.elevation + z.height)
        warn("zone_covers_opening", `${label} бүс ${o.id}-г хааж байна. Зориуд эсэхийг шалгана уу.`, { wall: z.wall, item: z.id });
    }
    for (const o of room.obstructions) {
      if (o.wall !== z.wall || !o.offset || !o.width || !o.elevation || !o.height) continue;
      if (
        z.offset < o.offset.um + o.width.um &&
        o.offset.um < z.offset + z.length &&
        z.elevation < o.elevation.um + o.height.um &&
        o.elevation.um < z.elevation + z.height
      )
        warn("zone_covers_obstruction", `${label} бүс ${o.id}-г хааж байна. Зориуд эсэхийг шалгана уу.`, { wall: z.wall, item: z.id });
    }
    const spans: [number, number, string][] = [];
    for (const p of z.partitions) {
      if (p.width <= 0) {
        err("zero_size", `${label}: хэсгийн өргөн 0 байна.`, { item: z.id });
        continue;
      }
      if (p.offset + p.width > z.length + tol.placementUm) err("partition_outside_zone", `${label}: хуваалт бүсээс хэтэрч байна.`, { item: z.id });
      for (const [a, b, other] of spans) {
        if (p.offset < b && a < p.offset + p.width) err("partitions_overlap", `${label}: ${other} ба ${p.id} хуваалт давхцаж байна.`, { item: z.id });
      }
      spans.push([p.offset, p.offset + p.width, p.id]);
    }
  }
  return out;
}

export function reviewProject(project: Project): FieldIssue[] {
  const out: FieldIssue[] = [];
  if (!project.site.address.trim()) out.push({ code: "missing_address", severity: "error", message: "Объектын хаягийг оруулна уу." });
  if (!project.rooms.length) out.push({ code: "no_rooms", severity: "error", message: "Дор хаяж нэг өрөө хэмжинэ үү." });
  for (const room of project.rooms) out.push(...reviewRoom(room));
  if (project.sign_off && !project.render.approved)
    out.push({ code: "sign_off_without_render", severity: "warning", message: "Гарын үсэг зурсан ч батлагдсан render хавсаргаагүй байна." });
  return out;
}

export function errorsOf(issues: FieldIssue[]): FieldIssue[] {
  return issues.filter((i) => i.severity === "error");
}
